import React, { useEffect, useState } from 'react';
import { Field, Formik, Form } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { ComboBox, Button, TextInput, Icons } from '@components';
import {
  RevisaoCPASchema,
  RevisaoCPAInitialValues,
  RevisaoCPADocs,
} from '@schemas';
import { enviaFormRequest } from '@store/user/actions';
import { mdiFileDownloadOutline } from '@mdi/js';
import { ModalUpload } from './components/ModalUpload';
import { validaUpload } from './utils/validaUpload';
import { RequerimentoService } from '../../../../common/services/requerimento';

const Anexos = ({ revisaoProcessoJudicial, foiJulgado }) => {
  const [doc, setDoc] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  useEffect(() => {
    if (revisaoProcessoJudicial === 'Não') {
      setDoc(RevisaoCPADocs.Administrativa);
    } else if (foiJulgado === 'Não') {
      setDoc(RevisaoCPADocs['Tutela Antecipada']);
    } else if (foiJulgado === 'Sim') {
      setDoc(RevisaoCPADocs.Definitiva);
    } else {
      setDoc([]);
    }
  }, [revisaoProcessoJudicial, foiJulgado]);

  return (
    <>
      <ModalUpload modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      {RevisaoCPADocs.commons.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                return (
                  <TextInput
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
      {doc.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                return (
                  <TextInput
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return form.setFieldValue(
                        anexo.name,
                        e.target.value ? e.currentTarget.files : null
                      );
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
    </>
  );
};

const service = new RequerimentoService();

export const RevisaoCPA = ({ title, reference }) => {
  const [fileURL, setFileURL] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    service
      .obterTermoRevisao()
      .then((URL) => setFileURL(URL))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="card">
      <div className="card-body">
        <h3 className="fw-bold ts-20 mb-32">{title}</h3>

        {loading ? (
          <p className="mb-4">Carregando termo de compromisso...</p>
        ) : (
          <a
            href={fileURL}
            rel="noreferrer"
            target="_blank"
            download
            title="Clique para termo de compromisso"
            className="w-250 mb-4 d-md-flex ts-14 mb-2 mt-4 align-items-center justify-content-between text-decoration-none fw-bold primary-color"
          >
            Baixar termo de compromisso
            <Icons path={mdiFileDownloadOutline} color="var(--primary-color)" />
          </a>
        )}

        <p className="mb-4">
          Venho, por meio deste, requerer à Fundação Forluminas de Seguridade
          Social – FORLUZ revisão em meu benefício de Complementação
          Proporcional de Aposentadoria do Plano Saldado (Plano A), em
          decorrência da revisão da minha aposentadoria e acréscimo de tempo de
          serviço pelo INSS. Para tanto, anexo a documentação necessária para
          tal fim.
        </p>
        <Formik
          validateOnMount
          initialValues={RevisaoCPAInitialValues}
          validationSchema={RevisaoCPASchema}
          onSubmit={(values) => {
            dispatch(
              enviaFormRequest({
                campos: values,
                formulario: reference,
                navigate,
              })
            );
          }}
        >
          {({
            handleSubmit,
            isValid,
            values,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form>
              <div className="row">
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="tempoContribuicaoINSS"
                    component={ComboBox}
                    label="Tempo de contribuição acrescido pelo INSS?"
                    options={['Sim', 'Não'].map((x) => {
                      return { value: x, label: x };
                    })}
                    onChange={(op) => {
                      if (op.value === 'Não') {
                        setFieldValue('revisaoProcessoJudicial', '', false);
                        setFieldValue('foiJulgado', '', false);

                        setFieldTouched(
                          'revisaoProcessoJudicial',
                          false,
                          false
                        );
                        setFieldTouched('foiJulgado', false, false);
                      }
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 col-lg-6">
                  <Field
                    disabled={!(values.tempoContribuicaoINSS === 'Sim')}
                    className="mb-3"
                    name="revisaoProcessoJudicial"
                    label="Revisão originária de processo judicial?"
                    component={ComboBox}
                    options={['Sim', 'Não'].map((x) => {
                      return { value: x, label: x };
                    })}
                    onChange={(op) => {
                      if (op.value === 'Não') {
                        setFieldValue('foiJulgado', '', false);

                        setFieldTouched('foiJulgado', false, false);
                      }
                    }}
                  />
                </div>

                <div className="col-md-12 col-lg-6">
                  <Field
                    disabled={!(values.revisaoProcessoJudicial === 'Sim')}
                    className="mb-3"
                    name="foiJulgado"
                    component={ComboBox}
                    label="Transitou em Julgado?"
                    options={['Sim', 'Não'].map((x) => {
                      return { value: x, label: x };
                    })}
                  />
                </div>
              </div>
              <Anexos
                foiJulgado={values.foiJulgado}
                revisaoProcessoJudicial={values.revisaoProcessoJudicial}
              />
              <hr className="mt-32 mt-xs-0 mb-32" />
              <div className="row">
                <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                  <div className="d-block d-md-flex align-items-center">
                    <Button
                      disabled={!isValid}
                      className="ms-0 ms-md-3"
                      onClick={handleSubmit}
                    >
                      Continuar
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
